/**
 * Design System - Chip Component
 * Reusable chip/tag component for selections, filters, and labels
 */

import React from 'react';
import {
  typographyColors,
  brandColors,
  strokeColors,
  spacing,
  borders,
  typography,
  fontPrimary,
} from '../../utils';

export interface ChipProps {
  label: string;
  isActive?: boolean;
  onClick?: () => void;
  icon?: React.ReactNode;
  variant?: 'default' | 'outlined';
  size?: 'sm' | 'md' | 'lg';
}

export const Chip: React.FC<ChipProps> = ({
  label,
  isActive = false,
  onClick,
  icon,
  size = 'md',
}) => {
  const sizeConfig = {
    sm: {
      paddingX: spacing[2],
      paddingY: spacing[1],
      fontSize: typography.size.xs,
    },
    md: {
      paddingX: spacing[3],
      paddingY: spacing[1.5],
      fontSize: typography.size.sm,
    },
    lg: {
      paddingX: spacing[4],
      paddingY: spacing[2],
      fontSize: typography.size.base,
    },
  };

  const config = sizeConfig[size];

  return (
    <button
      onClick={onClick}
      style={{
        backgroundColor: isActive ? brandColors.offset : '#FFFFFF',
        borderColor: isActive ? strokeColors.brand : strokeColors.base,
        borderWidth: borders.width.thin,
        borderStyle: 'solid',
        borderRadius: borders.radius.full,
        paddingLeft: config.paddingX,
        paddingRight: config.paddingX,
        paddingTop: config.paddingY,
        paddingBottom: config.paddingY,
        display: 'flex',
        alignItems: 'center',
        gap: spacing[1],
        cursor: onClick ? 'pointer' : 'default',
        transition: 'all 0.2s',
        fontFamily: fontPrimary,
        fontSize: config.fontSize,
        fontWeight: typography.weight.medium,
        color: typographyColors.primary,
      }}
      onMouseEnter={e => {
        if (onClick && !isActive) {
          e.currentTarget.style.backgroundColor = brandColors.offset;
        }
      }}
      onMouseLeave={e => {
        if (onClick && !isActive) {
          e.currentTarget.style.backgroundColor = '#FFFFFF';
        }
      }}
    >
      {icon && (
        <span style={{ display: 'flex', alignItems: 'center' }}>{icon}</span>
      )}
      <span>{label}</span>
    </button>
  );
};
